import React from 'react';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createFragmentContainer, graphql } from 'react-relay';

// components
import { SbSharedTitle } from '../SbSharedTitle/SbSharedTitle';
import { SbSharedPageBreadcrumbs } from '../SbSharedPageBreadcrumbs/SbSharedPageBreadcrumbs';
import { SbSharedBillboardAd } from '../SbSharedBillboardAd/SbSharedBillboardAd';
import { SbRespNullSearchResults } from '../SbRespNullSearchResults/SbRespNullSearchResults';
import { SharedRespMoreWaysToBrowse } from '../../shared/SharedRespMoreWaysToBrowse/SharedRespMoreWaysToBrowse';
import { SbRespNotificationUnavailableItem } from '../SbRespNotifications/SbRespNotificationUnavailableItem/SbRespNotificationUnavailableItem';
import { SharedRecentlyViewedItems } from '../../shared/SharedRecentlyViewedItems/SharedRecentlyViewedItems';
import { TradePromoLoader } from '../../global/TradePromo/TradePromoLoader';
import { SbSharedPersonalizedFilterToast } from '../SbSharedPersonalizedFilterToast/SbSharedPersonalizedFilterToast';
import { SbRespMainContent } from '../SbRespMainContent/SbRespMainContent';

// constants
import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants';
import { FILTER_REFINE_SEARCH } from '../SbSharedRefineMenu/sbSharedRefineMenuConstants';
import { MIN_THIN_PAGE_COUNT } from '../../constants/sbConstants';

const { SEARCH } = pageTypes;

// styles
import styles from '../SbRespComponents/SbRespComponents.scss';

function getSearchTerm(filters, isSearchPage) {
    const searchKey = isSearchPage ? SEARCH : FILTER_REFINE_SEARCH;

    return get(filters.filter(({ name }) => name === searchKey), '[0].values[0].urlLabel');
}

function SbRespCollectionsComponentsComponent(props) {
    const {
        viewer,
        currency,
        itemSearch,
        user,
        page,
        generatedUriRef,
        isClient,
        relay,
        relayVariables,
        shippingFilters,
        collectionsModules,
    } = props;

    const { totalResults, appliedFilters, pageType, internalBuyLinks = [] } = itemSearch;

    const { isBillboardAdEnabled, isMoreWaysToBrowseEnabled, isThinPageEnabled } = viewer;

    const isSearch = pageType === SEARCH;
    const showNullSearchResultPage = !totalResults;
    const isThinPage = totalResults < MIN_THIN_PAGE_COUNT && isThinPageEnabled;
    const searchTerm = getSearchTerm(appliedFilters, isSearch);
    const gridType = get(collectionsModules, 'gridType') || null;

    if (showNullSearchResultPage) {
        return (
            <SbRespNullSearchResults
                user={user}
                page={page}
                viewer={viewer}
                searchTerm={searchTerm}
                uriRef={generatedUriRef}
                itemSearch={itemSearch}
            />
        );
    }

    return (
        <React.Fragment>
            {/* Breadcrumbs */}
            <SbSharedPageBreadcrumbs itemSearch={itemSearch} />

            {/* Page Header */}
            <div className={styles.headerContainer}>
                <SbSharedTitle itemSearch={itemSearch} searchTerm={searchTerm} />
                {relayVariables.isTrade && isClient && (
                    <div className={styles.tradePromoMessage}>
                        <TradePromoLoader showIcon user={user} viewer={viewer} />
                    </div>
                )}
            </div>

            <SbRespMainContent
                currency={currency}
                itemSearch={itemSearch}
                viewer={viewer}
                user={user}
                isClient={isClient}
                isThinPage={isThinPage}
                isThinBrowsePage={isThinPage}
                relay={relay}
                gridType={gridType}
            />

            {/* Ad */}
            {!isThinPage && isBillboardAdEnabled && (
                <SbSharedBillboardAd itemSearch={itemSearch} adWidth="desktop" />
            )}

            {/* Recently Viewed Items */}
            {<SharedRecentlyViewedItems isClient={isClient} user={user} />}

            {/*  More Ways to Browse */}
            {isMoreWaysToBrowseEnabled && (
                <SharedRespMoreWaysToBrowse internalBuyLinks={internalBuyLinks} />
            )}

            {/* Unavailable Item Modal */}
            <SbRespNotificationUnavailableItem />

            {/* Personalized Filter Toast */}
            <SbSharedPersonalizedFilterToast shippingFilters={shippingFilters} />
        </React.Fragment>
    );
}

SbRespCollectionsComponentsComponent.propTypes = {
    itemSearch: PropTypes.object.isRequired,
    page: PropTypes.number,
    user: PropTypes.object,
    isClient: PropTypes.bool,
    viewer: PropTypes.object,
    generatedUriRef: PropTypes.string,
    relay: PropTypes.object.isRequired,
    currency: PropTypes.string.isRequired,
    internalBuyLinks: PropTypes.object,
    relayVariables: PropTypes.shape({
        isTrade: PropTypes.bool,
    }).isRequired,
    shippingFilters: PropTypes.array,
    collectionsModules: PropTypes.object,
};

const mapStateToProps = state => {
    const { filters, relayVariables } = state;

    return {
        generatedUriRef: filters.generatedUriRef,
        page: filters.page,
        relayVariables: relayVariables.variables,
    };
};

export const SbRespCollectionsComponents = createFragmentContainer(
    connect(mapStateToProps)(SbRespCollectionsComponentsComponent),
    {
        itemSearch: graphql`
            fragment SbRespCollectionsComponents_itemSearch on ItemSearchQueryConnection {
                totalResults
                pageType
                ...SbRespNullSearchResults_itemSearch
                ...SbSharedPageBreadcrumbs_itemSearch
                ...SbSharedTitle_itemSearch
                ...SbSharedBillboardAd_itemSearch
                ...SbRespTradeOnApprovalPromoBanner_itemSearch @include(if: $isTrade)
                ...SbRespMainContent_itemSearch
                appliedFilters {
                    name
                    values {
                        urlLabel
                    }
                }
                internalBuyLinks {
                    ...SharedRespMoreWaysToBrowse_internalBuyLinks
                }
            }
        `,
        user: graphql`
            fragment SbRespCollectionsComponents_user on User {
                canShowSellerName
                ...TradePromo_user @include(if: $isTrade)
                ...SbRespNullSearchResults_user
                ...SharedRecentlyViewedItems_user
                ...SbRespMainContent_user
            }
        `,
        viewer: graphql`
            fragment SbRespCollectionsComponents_viewer on Viewer {
                isBillboardAdEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isThinPageEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                isMoreWaysToBrowseEnabled: isEnabledByLocale(featureName: "NON_MVP_SEARCH_BROWSE")
                ...SbRespNullSearchResults_viewer
                ...TradePromo_viewer @include(if: $isTrade)
                ...SbRespMainContent_viewer
            }
        `,
        shippingFilters: graphql`
            fragment SbRespCollectionsComponents_shippingFilters on ShippingFilterType
                @relay(plural: true) {
                ...SbSharedPersonalizedFilterToast_shippingFilters
            }
        `,
        collectionsModules: graphql`
            fragment SbRespCollectionsComponents_collectionsModules on CollectionsModulesMainContent {
                gridType
            }
        `,
    }
);
